import type { TreeSelectNodeType } from "./treeSelectTypes";

export type TreeAction =
  | { type: "TOGGLE_NODE"; id: string; checked: boolean | "indeterminate" }
  | { type: "TOGGLE_ALL"; checked: boolean | "indeterminate" };

export type TreeState = TreeSelectNodeType[];

export function treeReducer(state: TreeState, action: TreeAction): TreeState {
  let updatedTree: TreeState;

  switch (action.type) {
    case "TOGGLE_NODE":
      updatedTree = updateNodeById(state, action.id, action.checked);
      break;
    case "TOGGLE_ALL":
      updatedTree = setAllNodes(state, action.checked === "indeterminate" ? true : action.checked);
      break;
    default:
      return state;
  }

  // Always recompute the entire tree after any change
  return recomputeTree(updatedTree);
}

export function initializeTreeState(nodes: TreeState): TreeState {
  return recomputeTree(nodes);
}

function updateNodeById(
  nodes: TreeSelectNodeType[],
  targetId: string,
  checked: boolean | "indeterminate",
): TreeSelectNodeType[] {
  return nodes.map((node) => {
    if (node.id === targetId && !node.disabled) {
      const updatedChildren = getUpdatedChildren(node.children, checked);
      return { ...node, selected: checked, children: updatedChildren };
    }

    if (node.children.length > 0) {
      const updatedChildren = updateNodeById(node.children, targetId, checked);
      if (updatedChildren !== node.children) {
        return { ...node, children: updatedChildren };
      }
    }

    return node;
  });
}

/**
 * Update children when parent is toggled
 */
function getUpdatedChildren(
  children: TreeSelectNodeType[],
  parentChecked: boolean | "indeterminate",
): TreeSelectNodeType[] {
  if (parentChecked === "indeterminate") {
    // Parent-only selection: unselect all children
    return setAllNodes(children, false);
  }

  return setAllNodes(children, parentChecked);
}

function sortLocations(nodes: TreeSelectNodeType[]): TreeSelectNodeType[] {
  return [...nodes].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Recursively recompute the entire tree bottom-up
 */
function recomputeTree(nodes: TreeSelectNodeType[]): TreeSelectNodeType[] {
  const processedNodes = nodes.map((node) => {
    let processedChildren = node.children;

    if (node.children.length > 0) {
      processedChildren = recomputeTree(node.children);
    }

    if (node.disabled) {
      return { ...node, children: sortLocations(processedChildren) };
    }

    if (processedChildren.length === 0) {
      return { ...node, children: processedChildren };
    }

    const activeChildren = processedChildren.filter((child) => !child.disabled);

    // When all children are disabled, can return here -- skips unnecessary logic on children
    if (activeChildren.length === 0) {
      return { ...node, children: sortLocations(processedChildren) };
    }

    const checkedChildren = activeChildren.filter((child) => child.selected === true);
    const indeterminateChildren = activeChildren.filter((child) => child.selected === "indeterminate");

    let computed: boolean | "indeterminate";

    if (checkedChildren.length === activeChildren.length) {
      computed = true;
    } else if (checkedChildren.length > 0 || indeterminateChildren.length > 0) {
      computed = "indeterminate";
    } else {
      computed = node.selected === "indeterminate" || node.selected === true ? "indeterminate" : false;
    }

    return {
      ...node,
      children: sortLocations(processedChildren),
      selected: computed,
    };
  });

  return sortLocations(processedNodes);
}

function setAllNodes(nodes: TreeSelectNodeType[], checked: boolean): TreeSelectNodeType[] {
  return nodes.map((node) => {
    let updatedChildren = node.children;
    if (node.children.length > 0) {
      updatedChildren = setAllNodes(node.children, checked);
    }

    if (node.disabled) {
      return { ...node, children: updatedChildren };
    }

    return { ...node, selected: checked, children: updatedChildren };
  });
}
